/**
 * chemistry-coach.ts — deterministic, fully-offline chemistry coach engine.
 *
 * Pure function. No I/O, no network. Takes the latest reading + optional
 * historical series and returns one plain-English coaching sentence, the
 * status (in-range / above / below), and the trend direction.
 */
import type { CoachParameter } from './ranges';
import { getRangeForCoachParameter } from './ranges';
import { computeTrend } from './trend';
import { buildSentence } from './templates';

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type { CoachParameter };

export interface CoachInput {
  /** Which parameter is being coached. */
  parameter: CoachParameter;
  value: number;
  unit: string;
  /** Optional historical series used for trend detection. */
  series?: ReadonlyArray<{ ts: string; value: number }>;
  /** How many days of history to use for the trend window (default 7). */
  windowDays?: number;
}

export interface CoachResult {
  sentence: string;
  status: 'in-range' | 'above' | 'below';
  trend: 'up' | 'down' | 'flat';
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

/**
 * Produce a coaching sentence for a single test-kit reading.
 * Deterministic and pure — no fetch, no side effects.
 */
export function coachReading(input: CoachInput): CoachResult {
  const { parameter, value, unit, series = [], windowDays = 7 } = input;

  // --- Range classification ------------------------------------------------
  const range = getRangeForCoachParameter(parameter);
  const status: CoachResult['status'] =
    value < range.min ? 'below' : value > range.max ? 'above' : 'in-range';

  // --- Trend detection -----------------------------------------------------
  const { direction: trend, perDayRate } = computeTrend(series, windowDays);

  // --- Sentence construction -----------------------------------------------
  const sentence = buildSentence({
    parameter,
    value,
    unit,
    status,
    trend,
    perDayRate,
    windowDays,
    range,
  });

  return { sentence, status, trend };
}
